import type { InteractiveBlock as InteractiveBlockType } from './types';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';

const RevealCards: React.FC<{ cards: { front: string; back: string }[] }> = ({ cards }) => {
  const [flipped, setFlipped] = useState<Record<number, boolean>>({});
  return (
    <div className="reveal-cards">
      {cards.map((card, i) => {
        const isFlipped = !!flipped[i];
        return (
          <motion.button
            type="button"
            key={i}
            className="reveal-cards__card"
            onClick={() => setFlipped((f) => ({ ...f, [i]: !f[i] }))}
            aria-pressed={isFlipped}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 + i * 0.08, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className={`reveal-cards__inner${isFlipped ? ' reveal-cards__inner--flipped' : ''}`}>
              <div className="reveal-cards__face reveal-cards__front">
                <span>{card.front}</span>
              </div>
              <div className="reveal-cards__face reveal-cards__back">
                <span>{card.back}</span>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

const TransformerRow: React.FC<{ row: { unsafe: string; safe: string }; index: number }> = ({ row, index }) => {
  const [revealed, setRevealed] = useState(false);
  const toggle = () => setRevealed((r) => !r);
  return (
    <motion.div
      className={`transformer-rows__row${revealed ? ' transformer-rows__row--revealed' : ''}`}
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      aria-expanded={revealed}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.06 + index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="transformer-rows__grid">
        <div className="transformer-rows__cell transformer-rows__unsafe">{row.unsafe}</div>
        <motion.div
          className="transformer-rows__arrow"
          aria-hidden="true"
          animate={{ rotate: revealed ? 0 : -45 }}
          transition={{ duration: 0.32, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12,5 19,12 12,19" />
          </svg>
        </motion.div>
        <AnimatePresence mode="wait" initial={false}>
          {revealed ? (
            <motion.div
              key="safe"
              className="transformer-rows__cell transformer-rows__safe"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.28 }}
            >
              {row.safe}
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              className="transformer-rows__cell transformer-rows__safe-placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              Tap to reveal
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const TransformerRows: React.FC<{ rows: { unsafe: string; safe: string }[] }> = ({ rows }) => (
  <div className="transformer-rows">
    {rows.map((row, i) => (
      <TransformerRow key={i} row={row} index={i} />
    ))}
  </div>
);

const Chips: React.FC<{ chips: string[] }> = ({ chips }) => (
  <div className="chips">
    {chips.map((chip, i) => (
      <motion.span
        key={chip}
        className="chips__chip"
        initial={{ opacity: 0, y: 12, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.1 + i * 0.08, duration: 0.42, ease: [0.34, 1.56, 0.64, 1] }}
      >
        {chip}
      </motion.span>
    ))}
  </div>
);

const InteractiveBlock: React.FC<{ block: InteractiveBlockType }> = ({ block }) => {
  if (block.kind === 'revealCards' && block.cards) {
    return <RevealCards cards={block.cards} />;
  }
  if (block.kind === 'transformerRows' && block.rows) {
    return <TransformerRows rows={block.rows} />;
  }
  if (block.kind === 'chips' && block.chips) {
    return <Chips chips={block.chips} />;
  }
  return null;
};

export default InteractiveBlock;
